<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      In this activity, you will work through some helpful information and complete a review task
      and two other tasks.
    </p>

    <h4 class="mb-5">Elements of Design:</h4>
    <p class="mb-5">There are 3 main elements of graphic design. They are:</p>
    <ul class="default-list">
      <li>Colours</li>
      <li>Imagery</li>
      <li>Typography</li>
    </ul>
    <p class="mb-5">
      Each element should support the design’s fundamental message. You’ve already learned a little
      about the use of colour in the first two activities.
    </p>
    <p class="mb-5">
      Now, let’s take a look at how to incorporate imagery and typography into your graphic designs
      in the following activities.
    </p>
    <h4 class="mb-5">Imagery</h4>
    <p class="mb-5">
      When we talk about imagery in graphic design, we are referring to the use of:
    </p>
    <ul class="default-list">
      <li>Photos</li>
      <li>Artwork</li>
      <li>Logos</li>
      <li>Icons</li>
      <li>Any other visual design elements</li>
    </ul>
    <p class="mb-5">Let’s take a look at how to use images in graphic design.</p>
    <p class="mb-5">
      Watch the following video about using images in your graphic designs. It will tell you where
      to find and how to choose suitable images. It will also discuss how you can change and modify
      a picture to better suit your design.
    </p>

    <responsive-youtube-wrapper :video="videos[1].video" :image="videos[1].image" />

    <p class="mb-5">
      Now that you know a little about how to choose images, let’s practice modifying an image.
      First, you’ll need to choose one. To do this, go to a free stock photos website such as
      Pexels.com:
    </p>
    <p class="mb-5">
      <a href="https://www.pexels.com" target="_blank" class="btn btn-secondary btn-size-medium"
        >Check out Pexels.com</a
      >
    </p>
    <p class="mb-5">
      To practise editing and working with images, you are going to use “Canva”. Canva is a
      fantastic graphic design platform used to create social media graphics, presentations,
      posters, documents, and other visual content. The app already includes lots of free templates
      and functions for users to use. You can pay more for increased functionality. For now, the
      free version is more than enough for your needs.
    </p>
    <p class="mb-5">Now, to set up a free Canva account, just follow the instructions below:</p>
    <ol class="numbered-list">
      <li>
        Type a keyword into the search bar for something you’re interested in. It could be anything.
        Any image will do for now.
      </li>
      <li>
        When you’ve hit the search button, look through the generated results. Some will be
        sponsored results (these cost money) and some will be free.
      </li>
      <li>Choose an image and download it onto your computer</li>
      <li>Now, go to https://www.canva.com/</li>
      <li>Using your email, set up a free account with Canva and log in.</li>
    </ol>
    <p class="my-5">
      Once you’ve set up your free Canva account, it’s time to upload your stock photo.
    </p>
    <ol class="numbered-list">
      <li>Click the “Create Design” button on the top right of your screen</li>
      <li>Click the “Start with an image” option</li>
      <li>
        Locate your stock photo file in your downloads folder and double click to upload to Canva
      </li>
      <li>Click on “Edit image”</li>
      <li>
        Use the tool power to make various adjustments to your image. You can use Canva to:
        <ul class="default-list mt-5">
          <li>Change the background colour</li>
          <li>Add effects</li>
          <li>Filter</li>
          <li>Adjust brightness, colour, saturation, etc</li>
          <li>Crop</li>
          <li>Flip</li>
        </ul>
      </li>
    </ol>
    <p class="mb-5">
      Play around with the various features until you feel comfortable modifying images. You can
      then save and download your image to compare with the original.
    </p>
    <p class="mb-5">
      To find out more about how to use Canva to edit your image, use the following link:
    </p>
    <p class="mb-5">
      <a
        href="https://www.canva.com/photo-editor/"
        target="_blank"
        class="btn btn-secondary btn-size-medium"
        >Try Canva Photo Editor</a
      >
    </p>
    <h4 class="mb-5">Typography</h4>
    <p class="mb-5">
      Before we start our next mini-project, we’re going to take a look at typography. To learn some
      of the basics of typography, watch the following fun video:
    </p>

    <responsive-youtube-wrapper :video="videos[2].video" :image="videos[2].image" />

    <h4 class="mb-5">I Shot the Serif - But I did not shoot the sans serif</h4>
    <p class="mb-5">
      If you watched the video carefully, you’d now have a good understanding of the difference
      between serif and sans serif fonts. Let’s check your understanding with a fun game to help
      hone your recognition skills. Hit the link below:
    </p>
    <p class="mb-5">
      <a
        href="https://www.tothepoint.co.uk/us/fun/i-shot-the-serif/"
        target="_blank"
        class="btn btn-secondary btn-size-medium"
        >Play I Shot the Serif</a
      >
    </p>
    <h4 class="mb-5">Canva Fonts</h4>
    <p class="mb-5">
      To learn a little more about the types of fonts available on Canva and how to use them, check
      out the article below - what you learn here will be very useful for your project!
    </p>
    <p class="mb-5">
      <a
        href="https://www.canva.com/learn/canva-for-work-brand-fonts/"
        target="_blank"
        class="btn btn-secondary btn-size-medium"
        >Learn more about Fonts</a
      >
    </p>
    <h4 class="mb-5">Layout and Composition</h4>
    <p class="mb-5">
      The unsung heroes of graphic design, layout, and composition play an important role in
      learning how to structure your designs for maximum impact. Learn some of the fundamentals of
      layout and composition in the following short video.
    </p>

    <responsive-youtube-wrapper :video="videos[3].video" :image="videos[3].image" />
    <p class="mb-5">
      <strong>Review Task:</strong> Look at the key elements of layout and composition. Can you
      match the concept with the definition? (Drag the layout/composition description at the top
      down into the box opposite its correct name).
    </p>

    <draggable class="list-group mb-5" :list="list1" group="people">
      <div class="list-group-item" v-for="element in list1" :key="element.name">
        <span>{{ element.name }}</span>
      </div>
    </draggable>

    <div
      class="vx-row flex items-center justify-center defaultclass"
      v-for="(question, index) in questions"
      :key="index"
      :class="{ wrong: !correct_answers['question' + index] }"
    >
      <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-2">{{ question.question }}</div>
      <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-2">
        <draggable
          class="list-group draggable-area"
          :list="answers['question' + index]"
          group="people"
          @change="removeOldSelection(index)"
        >
          <div
            class="list-group-item"
            v-for="element in answers['question' + index]"
            :key="element.name"
          >
            <span>{{ element.name }}</span>
          </div>
        </draggable>
        <match-select
          :pool="list1"
          :answer="answers['question' + index]"
          :label="question.question"
          :editable="isEditable"
        />
      </div>
    </div>
    <div class="overflow-hidden my-5" v-if="isEditable">
      <button class="btn btn-secondary danger btn-size-medium" @click="resetAnswers">Reset</button>
    </div>

    <h4 class="mb-5">Graphic Design Projects</h4>
    <p class="mb-5">
      Before starting your project, work your way through this very useful video to learn a little
      more about some of the functionalities of Canva as a design tool.
    </p>
    <p class="mb-5">How to use Canva for Beginners - Learn Canva in 10 Minutes!</p>

    <responsive-youtube-wrapper :video="videos[4].video" :image="videos[4].image" />

    <h4 class="mb-5">Task 1: Graphic Design – Inspirational Quote Project:</h4>
    <p class="mb-5">
      Now, for your first project, you are going to combine the text of an inspirational quote with
      a suitable image using Canva.
    </p>
    <p class="mb-5">
      You can find lots of inspirational quotes by doing a simple Google search online.
      Goodreads.com has lots of good ones here:
    </p>
    <p class="mb-5">
      <a
        href="https://www.goodreads.com/quotes/tag/inspirational"
        target="_blank"
        class="btn btn-secondary btn-size-medium"
        >Go to Inspirational Quotes</a
      >
    </p>
    <p class="mb-5">
      When you’ve chosen a suitably inspiring quote to work with, you need to find an image to go
      along with it. Think of some suitable keywords and start searching free stock photo websites
      like Pexels or Pixabay.
    </p>
    <p class="mb-5">
      When you’ve found your image and uploaded it to your Canva account, you’re ready to start.
      Using all you’ve learned in your virtual job experience, your task is to combine the text and
      image in an inspiring design.
    </p>
    <p class="mb-5">
      Remember to incorporate what you’ve learned about colour theory, editing images, typography,
      and layout and composition in your design.
    </p>
    <p class="mb-5">
      You need to download your final design and then upload it at the bottom of this page.
    </p>

    <h4 class="mb-5">Task 2: Working with a Design Brief</h4>
    <p class="mb-5">
      Graphic Designers develop ideas and products for clients based on a “Design Brief” – basically
      a document created by a client outlining what they need done.
    </p>

    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <p class="mb-5">
          In this activity, you are going to work with a small business owner by the name of Josh
          Delevigne who has a small dog walking business.
        </p>
        <p class="mb-5">
          Josh wants to advertise his business and just submitted a design brief to you and asked
          you to help him with this design.
        </p>
        <p class="mb-5">You can download the design brief below:</p>
        <p class="mb-5">
          <a
            :href="'content/creativeindustries/jd_design_brief.pdf' | blob_url"
            target="_blank"
            class="btn btn-secondary btn-size-medium"
            >Download Design Brief</a
          >
        </p>
        <p class="mb-5">
          You can use any software you like (e.g. Canva, Photoshop, or if you are really stuck, you
          can even use Word as a last option).
        </p>
        <p class="mb-5">
          Create a design based on Josh’s design brief requests, save it, and upload it below.
        </p>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <img
          src="@/assets/images/experiences/creativeindustries/josh_dog_walking.jpg"
          class="img-responsive mx-auto mb-10"
          alt=""
        />
      </div>
    </div>

    <h4 class="mb-5">Uploads:</h4>
    <p class="mb-5">For Activity/Task 1: Inspirational Quote</p>
    <fileupload-button-immediate
      v-model="answers['uploaded_file_1']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Inspirational Quote'"
      filetype="photo_doc"
      index="1"
    />

    <p class="mb-5">For Activity/Task 2: Working With a Design Brief</p>
    <fileupload-button-immediate
      v-model="answers['uploaded_file_2']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Josh’s Design'"
      filetype="photo_doc"
      index="2"
    />

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import draggable from 'vuedraggable';
import MatchSelect from '@/components/UI/MatchSelect';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  components: { draggable, MatchSelect },
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/creativeindustries/images_splash.jpg'),
          video: 'https://www.youtube.com/embed/MELKuexR3sQ',
        },
        2: {
          image: require('@/assets/images/experiences/creativeindustries/typography_splash.jpg'),
          video: 'https://www.youtube.com/embed/sByzHoiYFX0',
        },
        3: {
          image: require('@/assets/images/experiences/creativeindustries/layout_and_composition_splash.jpg'),
          video: 'https://www.youtube.com/embed/a5KYlHNKQB8',
        },
        4: {
          image: require('@/assets/images/experiences/creativeindustries/canva_for_beginners_splash.jpg'),
          video: 'https://www.youtube.com/embed/zJSgUx5K6V0',
        },
      },
      questions: {
        1: { question: 'Proximity' },
        2: { question: 'White Space' },
        3: { question: 'Alignment' },
        4: { question: 'Contrast' },
        5: { question: 'Hierarchy' },
        6: { question: 'Repetition' },
      },
      answers: {
        question1: [],
        question2: [],
        question3: [],
        question4: [],
        question5: [],
        question6: [],
        uploaded_file_1: '',
        uploaded_file_2: '',
      },
      files: {
        file1: '',
        file2: '',
      },
      correct_answers: {
        question1: true,
        question2: true,
        question3: true,
        question4: true,
        question5: true,
        question6: true,
      },
      list1: [
        { name: `Using visual space to show relationships`, id: 1 },
        { name: `Negative space in a design`, id: 2 },
        { name: `How text and graphics are lined up`, id: 3 },
        { name: `The difference between elements in a design`, id: 4 },
        { name: `Shows the viewer what order to view each element`, id: 5 },
        { name: `A consistent look and feel`, id: 6 },
      ],
    };
  },
  methods: {
    save() {
      this.data = this.answers;
      this.saveActivity(this.$route.params.slug, this.data, -1);
    },
    removeOldSelection(index) {
      if (this.answers['question' + index].length > 1) {
        let lastElem =
          this.answers['question' + index][this.answers['question' + index].length - 1];
        for (let i = 0; i < this.answers['question' + index].length - 1; i++) {
          this.list1.push(this.answers['question' + index][i]);
        }
        this.answers['question' + index] = [];
        this.answers['question' + index].push(lastElem);
      }
      this.checkAnswers();
    },
    checkAnswers() {
      for (let i = 1; i <= 6; i++) {
        if (this.answers['question' + i].length) {
          if (this.answers['question' + i][0].id != i) {
            this.correct_answers['question' + i] = false;
          } else {
            this.correct_answers['question' + i] = true;
          }
        }
      }
    },
    resetAnswers() {
      for (let index = 1; index <= 6; index++) {
        if (this.answers['question' + index].length > 0) {
          let lastElem = this.answers['question' + index][0];
          this.answers['question' + index] = [];
          this.correct_answers['question' + index] = true;
          this.list1.push(lastElem);
        }
      }
      this.list1 = this.list1.sort(() => 0.5 - Math.random());
    },
  },
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.data.question1 !== 'undefined') {
      this.answers = this.data;

      for (let i = 1; i <= 6; i++) {
        if (this.answers['question' + i].length > 0) {
          let id = this.answers['question' + i][0].id;
          this.list1 = this.list1.filter(function (item) {
            return item.id !== id;
          });
        }
      }
      this.checkAnswers();
    }
    //Shuffle the answers array
    this.list1 = this.list1.sort(() => 0.5 - Math.random());
    this.checkAnswers();
  },
};
</script>

<style scoped>
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 2px;
  margin: 0;
}
.defaultclass {
  margin: 0;
}
.list-group {
  display: -ms-flexbox;
  display: -webkit-box;
  display: flex;
  -ms-flex-direction: column;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  flex-direction: column;
  padding-left: 0;
  margin-bottom: 0;
}
.list-group-item {
  position: relative;
  display: block;
  padding: 0.75rem 1.25rem;
  margin-bottom: -1px;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.125);
}
.list-group.draggable-area {
  border: 1px dashed #ccc;
  min-height: 45px;
}
</style>
